'use client';

import { bunja, createScope } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtomValue } from 'jotai';
import Link from 'next/link';

import { InstrumentApi } from '@flipster/apis/v2/snapshot';
import { tieredMaxOiFlag } from '@flipster/features/src/flag';
import { zendesk } from '@flipster/features/src/shared/zendesk/url';
import { pswapsBunja } from '@flipster/socket/r230522/pswaps';
import { css } from '@flipster/styled-system/css';
import { stack } from '@flipster/styled-system/patterns';
import { Price } from '@flipster/ui';

interface Props {
  product: InstrumentApi;
  quoteCurrency: string;
}

export const NotionalMaxOrderQuantity = ({ product, quoteCurrency }: Props) => {
  const { notionalMaxOrderAmountAtom } = useBunja(rowDataBunja, [ProductScope.bind(product)]);
  const notionalMaxOrderAmount = useAtomValue(notionalMaxOrderAmountAtom);
  return <PriceAndDescription price={notionalMaxOrderAmount} currency={quoteCurrency} />;
};

export const NotionalMaxPositionSize = ({ product, quoteCurrency }: Props) => {
  const { notionalMaxPositionAtom } = useBunja(rowDataBunja, [ProductScope.bind(product)]);
  const notionalMaxPosition = useAtomValue(notionalMaxPositionAtom);
  return <PriceAndDescription price={notionalMaxPosition} currency={quoteCurrency} />;
};

interface PriceAndDescriptionProps {
  price: string;
  currency: string;
}
const PriceAndDescription = ({ price, currency }: PriceAndDescriptionProps) => {
  return (
    <div className={stack({ gap: 6 })}>
      <Price price={price} currency={currency} />
      {tieredMaxOiFlag() && (
        <p className={css({ textStyle: 'Text/M/Regular', color: 'content.default.level-1' })}>
          These values represent the platform-wide maximums.
          <br />
          Your actual order and position limits may vary based on your assigned trading tier.
          <br />
          Learn more in our <Link href={zendesk.getUrl('TradingRules')}>trading rules.</Link>
        </p>
      )}
    </div>
  );
};

const ProductScope = createScope<InstrumentApi>();

const rowDataBunja = bunja(() => {
  const product = bunja.use(ProductScope);
  const pswapsBunjaInstance = bunja.use(pswapsBunja);
  const { pswapsAtom } = pswapsBunjaInstance;

  const notionalMaxOrderAmountAtom = atom((get) => {
    const pswaps = get(pswapsAtom);
    return pswaps?.[product.symbol]?.notionalMaxOrderAmount || product.notionalMaxOrderAmount;
  });

  const notionalMaxPositionAtom = atom((get) => {
    const pswaps = get(pswapsAtom);
    return pswaps?.[product.symbol]?.notionalMaxPosition || product.notionalMaxPosition;
  });

  return {
    notionalMaxOrderAmountAtom,
    notionalMaxPositionAtom,
  };
});
rowDataBunja.debugLabel = 'rowDataBunja';
